import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsTextProps } from '@mui/x-charts/ChartsText';
import { Team } from '@internal/schemas-common';
import { useNavigate } from 'react-router-dom';

import { Box, useTheme } from '@material-ui/core';
import { createContext, ReactNode, useContext, useMemo } from 'react';

interface ClickableAxisLabelContextValue {
  lookupTeam: (name: string) => Team | undefined;
  routeSuffix: string;
}

const ClickableAxisLabelContext = createContext<ClickableAxisLabelContextValue>(
  {
    lookupTeam: () => undefined,
    routeSuffix: '',
  },
);

function ClickableAxisLabelProvider({
  children,
  chartData,
  routeSuffix,
}: {
  children: ReactNode;
  chartData: Array<{ teamName: string; team: Team | undefined }>;
  routeSuffix: string;
}) {
  const lookupTeam = useMemo(
    () => (name: string) => chartData.find(d => d.teamName === name)?.team,
    [chartData],
  );

  return (
    <ClickableAxisLabelContext.Provider value={{ lookupTeam, routeSuffix }}>
      {children}
    </ClickableAxisLabelContext.Provider>
  );
}

function ClickableAxisLabel(props: ChartsTextProps) {
  const { text, x, y, style, ...rest } = props;
  const { lookupTeam, routeSuffix } = useContext(ClickableAxisLabelContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const team = text ? lookupTeam(text) : undefined;

  if (!team) {
    return (
      <text x={x} y={y} style={style as any} {...rest}>
        {text}
      </text>
    );
  }

  return (
    <text
      x={x}
      y={y}
      {...rest}
      onClick={() => navigate(`${team.portalTeamPageHref}/${routeSuffix}`)}
      style={
        {
          ...style,
          cursor: 'pointer',
          fill: theme.palette.primary.main,
          fontSize: theme.typography.button.fontSize,
        } as any
      }
    >
      {text}
    </text>
  );
}

export interface StackedBarChartSeries {
  data: number[];
  label: string;
  stack?: string;
  barLabel?: 'value';
}

export interface StackedBarChartProps {
  chartData: Array<{ teamName: string; team: Team | undefined }>;
  series: StackedBarChartSeries[];
  routeSuffix: string;
  colors?: string[];
  heightPerTeam?: number;
  xAxisLabel?: string;
}

export function StackedBarChart({
  chartData,
  series,
  routeSuffix,
  colors,
  heightPerTeam = 50,
  xAxisLabel,
}: StackedBarChartProps) {
  const chartHeight = Math.max(300, chartData.length * heightPerTeam + 100);
  const maxTeamNameLength = Math.max(
    0,
    ...chartData.map(d => d.teamName.length),
  );
  const yAxisWidth = Math.max(60, maxTeamNameLength * 7);
  const maxContainerHeight = 600;

  return (
    <Box
      style={{
        width: '100%',
        maxHeight: maxContainerHeight,
        overflowY: chartHeight > maxContainerHeight ? 'auto' : 'visible',
        overflowX: 'hidden',
      }}
    >
      <ClickableAxisLabelProvider
        chartData={chartData}
        routeSuffix={routeSuffix}
      >
        <BarChart
          colors={colors}
          layout="horizontal"
          xAxis={[{ scaleType: 'linear', label: xAxisLabel }]}
          yAxis={[
            {
              scaleType: 'band',
              data: chartData.map(d => d.teamName),
              tickLabelStyle: { textAnchor: 'end' },
              width: yAxisWidth,
            },
          ]}
          series={series}
          slots={{ axisTickLabel: ClickableAxisLabel }}
          height={chartHeight}
        />
      </ClickableAxisLabelProvider>
    </Box>
  );
}
